import type { CSSProperties } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import type { DailyUsagePoint, UsageGroupRow } from '@/types';
import { fmtUsd, fmtNumber } from '@/lib/format';

// Key Travel brand data colors (single-hue magnitude charts; each chart page
// also renders a data table, which provides the accessible fallback).
//
// CSS variables rather than literals: Recharts forwards these to SVG fill/stroke attributes,
// where var() resolves natively — so the charts follow the theme without a re-render or a
// JS colour lookup. Without this the graphs stay light-on-light in dark mode.
export const chartColors = {
  green: 'var(--kt-chart-1)',
  grid: 'var(--kt-chart-grid)',
  axis: 'var(--kt-chart-axis)'
};

const tooltipStyle: CSSProperties = {
  background: 'var(--kt-surface)',
  border: '1px solid var(--kt-grey-200)',
  color: 'var(--kt-grey-900)',
  borderRadius: 6,
  fontSize: '.85rem',
  boxShadow: 'var(--shadow-md)'
};

const axisTick = { fontSize: 12, fill: chartColors.axis };

/** YYYY-MM-DD → MM-DD for compact axis ticks. */
function dateTick(d: string): string {
  return d.length >= 10 ? d.slice(5, 10) : d;
}

const usdTick = (v: number) => fmtUsd(v);
const usdValue = (v: number | string) => fmtUsd(Number(v));
const numValue = (v: number | string) => fmtNumber(Number(v));

/** Daily cost over time as a single-series area chart. */
export function DailyCostChart({ data }: { data: DailyUsagePoint[] }) {
  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 0 }}>
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={dateTick} tick={axisTick} axisLine={false} tickLine={false} minTickGap={28} />
          <YAxis tickFormatter={usdTick} tick={axisTick} axisLine={false} tickLine={false} width={72} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number | string) => [usdValue(v), 'Cost']} />
          <Area
            type="monotone"
            dataKey="costUsd"
            stroke={chartColors.green}
            strokeWidth={2}
            fill={chartColors.green}
            fillOpacity={0.12}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Daily call count as a single-series column chart. */
export function DailyCallsChart({ data }: { data: DailyUsagePoint[] }) {
  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={dateTick} tick={axisTick} axisLine={false} tickLine={false} minTickGap={28} />
          <YAxis tickFormatter={(v: number) => fmtNumber(v)} tick={axisTick} axisLine={false} tickLine={false} width={56} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,104,69,.06)' }} formatter={(v: number | string) => [numValue(v), 'Calls']} />
          <Bar dataKey="calls" fill={chartColors.green} radius={[4, 4, 0, 0]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Cost per group (model/caller) as horizontal bars — best when names are long. */
export function GroupCostBarChart({ data, label }: { data: UsageGroupRow[]; label: string }) {
  const rows = [...data].sort((a, b) => b.costUsd - a.costUsd);
  const height = Math.max(160, rows.length * 36 + 40);
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 28, left: 8, bottom: 0 }}>
          <CartesianGrid stroke={chartColors.grid} horizontal={false} />
          <XAxis type="number" tickFormatter={usdTick} tick={axisTick} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="group" width={170} tick={axisTick} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,104,69,.06)' }} formatter={(v: number | string) => [usdValue(v), label]} />
          <Bar dataKey="costUsd" fill={chartColors.green} radius={[0, 4, 4, 0]} maxBarSize={20} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Cost per group as vertical columns (compact category names). */
export function GroupCostColumnChart({ data, label }: { data: UsageGroupRow[]; label: string }) {
  const rows = [...data].sort((a, b) => b.costUsd - a.costUsd);
  const truncate = (s: string) => (s.length > 14 ? `${s.slice(0, 13)}…` : s);
  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 8, right: 16, left: 4, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="group" tickFormatter={truncate} tick={{ ...axisTick, fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
          <YAxis tickFormatter={usdTick} tick={axisTick} axisLine={false} tickLine={false} width={72} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,104,69,.06)' }} formatter={(v: number | string) => [usdValue(v), label]} />
          <Bar dataKey="costUsd" fill={chartColors.green} radius={[4, 4, 0, 0]} maxBarSize={40} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
